import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectObjectsState } from "app/data/selectors";
import { UtilityService } from "app/data/services";
import { updateResolvedAddresses } from "app/features";

const useMarkerResolvedAddress = (selectedMarker: any) => {
  const dispatch = useAppDispatch();
  const { resolvedAddresses } = useAppSelector(selectObjectsState);

  // Separate loading state for marker
  const [markerAddressLoading, setMarkerAddressLoading] = useState(false);
  const hasResolvedMarkerAddress = useRef(false);
  const lastMarkerKey = useRef<string>("");

  useEffect(() => {
    if (
      !selectedMarker?.location?.latitude ||
      !selectedMarker?.location?.longitude
    ) {
      hasResolvedMarkerAddress.current = false;
      lastMarkerKey.current = "";
      setMarkerAddressLoading(false);
      return;
    }

    const currentMarkerKey = `${selectedMarker.location.latitude},${selectedMarker.location.longitude}`;

    // Reset if marker changed
    if (currentMarkerKey !== lastMarkerKey.current) {
      hasResolvedMarkerAddress.current = false;
      lastMarkerKey.current = currentMarkerKey;
    }

    if (!hasResolvedMarkerAddress.current && !markerAddressLoading) {
      // Check if address already exists in global state
      const existingAddress = resolvedAddresses.find(
        (address: any) =>
          address.latitude === selectedMarker.location.latitude &&
          address.longitude === selectedMarker.location.longitude
      );

      if (!existingAddress) {
        setMarkerAddressLoading(true);

        // Call UtilityService directly instead of using Redux thunk
        UtilityService.getAddressFromLatLng(
          selectedMarker.location.latitude,
          selectedMarker.location.longitude
        )
          .then((response) => {
            if (response.status === 200) {
              // Manually update the global resolved addresses without triggering loading state
              dispatch(
                updateResolvedAddresses({
                  latitude: selectedMarker.location.latitude,
                  longitude: selectedMarker.location.longitude,
                  address: response.data.resolvedAddress
                })
              );
            }
          })
          .catch((error) => {
            console.error("Failed to resolve marker address:", error);
          })
          .finally(() => {
            setMarkerAddressLoading(false);
            hasResolvedMarkerAddress.current = true;
          });
      } else {
        hasResolvedMarkerAddress.current = true;
      }
    }
  }, [selectedMarker, resolvedAddresses, dispatch, markerAddressLoading]);

  return { resolvedAddresses, markerAddressLoading };
};

export default useMarkerResolvedAddress;
